// Staff /meditations view. Browse all gallery pancakes, sort by recommended /
// chronological / by shift, tap into one to write or edit a meditation. Also
// surfaces the "marked for meditation" flag set from the Artist view.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection, doc, getDoc, limit, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where,
} from "firebase/firestore";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { meditationScore, shortLabel } from "../utils/meditations";

import { STAFF_THEME, FONT } from "../theme/staff";
// MeditationsView keeps cream-tinted border/muted/dim so the view feels like
// a quieter, more contemplative cousin of the rest of the staff palette.
const BRAND = {
  ...STAFF_THEME,
  border: "rgba(255,218,173,0.18)",
  muted:  "rgba(255,218,173,0.55)",
  dim:    "rgba(255,218,173,0.4)",
  blue:   STAFF_THEME.sky,
};

// Default sort for each view — switching views resets sort to that view's
// natural default so Dan isn't stuck on an option that doesn't make sense.
// "To meditate" defaults to ⭐ Showpieces: the pieces Dan deliberately starred
// in the Library are the ones worth reflecting on, and a short curated list is
// far easier to act on than the full backlog. Falls back to "recommended" if
// nothing is starred yet (see effect below) so the view is never empty.
const DEFAULT_SORT = { unmeditated: "showpiece", meditated: "recent", shifts: "all" };

export default function MeditationsView({ navigate }) {
  const [orders, setOrders] = useState([]);
  const [shifts, setShifts] = useState({}); // id → summary doc
  const [loaded, setLoaded] = useState(false);
  const [view, setView]     = useState("unmeditated"); // unmeditated | meditated | shifts
  const [sort, setSort]     = useState(DEFAULT_SORT.unmeditated);
  const [editing, setEditing] = useState(null); // order being edited
  const [editingShift, setEditingShift] = useState(null); // shift being edited (Shifts tab)

  // Swap sort to the view's default when tabs change.
  const switchView = (nextView) => {
    setView(nextView);
    setSort(DEFAULT_SORT[nextView]);
  };

  // Subscribe to all done orders with a photo
  useEffect(() => {
    const q = query(
      collection(db, "orders"),
      where("state", "==", "done")
    );
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((o) => (o.photo_url || o.photo_cutout_url) && !o.hidden && !o.trashedAt);
      setOrders(rows);
      setLoaded(true);
    });
    return unsub;
  }, []);

  // Deep-link: ?edit=orderId auto-opens the meditation editor for that pancake.
  // Used by the library view's "Write meditation" button.
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (!loaded || deepLinkHandled.current) return;
    const sp = new URLSearchParams(window.location.search);
    const editId = sp.get("edit");
    if (!editId) return;
    const target = orders.find(o => o.id === editId);
    if (target) {
      deepLinkHandled.current = true;
      setEditing(target);
      // Clean up URL so refresh doesn't re-open
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loaded, orders]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to shift summaries (lightweight) for grouping + per-shift draw avg
  useEffect(() => {
    const q = query(collection(db, "shift_summaries"), orderBy("closedAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const m = {};
      snap.docs.forEach((d) => { m[d.id] = { id: d.id, ...d.data() }; });
      setShifts(m);
    });
    return unsub;
  }, []);

  // Shifts tab — the full shift docs (meditation lives there, not on the
  // summary mirror). orderBy("closedAt") naturally excludes the active shift.
  // Subscribed only while the tab is open.
  const [shiftDocs, setShiftDocs] = useState([]);
  useEffect(() => {
    if (view !== "shifts") return;
    const q = query(collection(db, "shifts"), orderBy("closedAt", "desc"), limit(150));
    const unsub = onSnapshot(q, (snap) => {
      setShiftDocs(
        snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((s) => !s._isTestData)
      );
    });
    return unsub;
  }, [view]);

  const shiftRows = useMemo(() => {
    if (view !== "shifts") return [];
    if (sort === "meditated")   return shiftDocs.filter((s) => s.meditation?.trim());
    if (sort === "unmeditated") return shiftDocs.filter((s) => !s.meditation?.trim());
    return shiftDocs;
  }, [shiftDocs, sort, view]);

  // Per-shift draw average for scoring
  const shiftAvgs = useMemo(() => {
    const sums = {}, counts = {};
    for (const o of orders) {
      if (o.shiftId && o.drawingTimeMs) {
        sums[o.shiftId]   = (sums[o.shiftId]   || 0) + o.drawingTimeMs;
        counts[o.shiftId] = (counts[o.shiftId] || 0) + 1;
      }
    }
    const out = {};
    for (const id of Object.keys(sums)) out[id] = sums[id] / counts[id];
    return out;
  }, [orders]);

  // Split pools by meditation presence. "To meditate" = work without a
  // written meditation yet; "Meditated" = work Dan has already reflected on.
  const unmeditatedOrders = useMemo(
    () => orders.filter((o) => !o.meditation || !o.meditation.trim()),
    [orders]
  );
  const meditatedOrders = useMemo(
    () => orders.filter((o) => o.meditation && o.meditation.trim()),
    [orders]
  );

  const sourceOrders = view === "unmeditated" ? unmeditatedOrders : meditatedOrders;

  // Showpieces awaiting a meditation. Drives the default-view fallback below.
  const showpieceCount = useMemo(
    () => unmeditatedOrders.filter((o) => o.isShowcase).length,
    [unmeditatedOrders]
  );

  // Never-empty guard (mirrors the Pancakeopedia showpieces rule): if Dan
  // hasn't starred anything yet, the showpiece default would show a blank wall,
  // so fall back once to "recommended" after the first load. Runs only while
  // still on the untouched default — a manual sort pick is never overridden.
  const showpieceFallbackDone = useRef(false);
  useEffect(() => {
    if (!loaded || showpieceFallbackDone.current) return;
    showpieceFallbackDone.current = true;
    if (view === "unmeditated" && sort === "showpiece" && showpieceCount === 0) {
      setSort("recommended");
    }
  }, [loaded, showpieceCount, view, sort]);

  // "Recommended" for Unmeditated uses weighted sampling (elevation + noise)
  // so Dan doesn't see the same top-scored pancakes every visit. Other
  // sort modes are deterministic.
  const sorted = useMemo(() => {
    const scored = sourceOrders.map((o) => ({
      o,
      score: meditationScore(o, { shiftAvgDrawMs: o.shiftId ? shiftAvgs[o.shiftId] : null }),
    }));

    // Unmeditated view sort modes
    if (view === "unmeditated") {
      if (sort === "showpiece") {
        return scored
          .filter((x) => x.o.isShowcase)
          .sort((a, b) => tsMs(b.o) - tsMs(a.o))
          .map((x) => x.o);
      }
      if (sort === "marked") {
        return scored
          .filter((x) => x.o.meditationMark)
          .sort((a, b) => tsMs(b.o) - tsMs(a.o))
          .map((x) => x.o);
      }
      if (sort === "recent") {
        return scored.sort((a, b) => tsMs(b.o) - tsMs(a.o)).map((x) => x.o);
      }
      if (sort === "shift") {
        return scored.sort((a, b) => {
          const sa = a.o.shiftId || "";
          const sb = b.o.shiftId || "";
          if (sa !== sb) return sb.localeCompare(sa);
          return tsMs(b.o) - tsMs(a.o);
        }).map((x) => x.o);
      }
      // Recommended — weighted random sample elevates high-score pancakes
      // but keeps a degree of randomness so the queue feels alive.
      const items = scored.map((x) => ({ ...x.o, _score: x.score }));
      const result = [];
      const pool = [...items];
      while (pool.length > 0) {
        const totalWeight = pool.reduce((a, b) => a + Math.max(0.5, b._score + 2), 0);
        let r = Math.random() * totalWeight;
        let idx = 0;
        for (; idx < pool.length; idx++) {
          r -= Math.max(0.5, pool[idx]._score + 2);
          if (r <= 0) break;
        }
        if (idx >= pool.length) idx = pool.length - 1;
        result.push(pool[idx]);
        pool.splice(idx, 1);
      }
      return result;
    }

    // Meditated view sort modes
    if (sort === "pinned") {
      return scored
        .filter((x) => x.o.meditationPinned)
        .sort((a, b) => meditatedAtMs(b.o) - meditatedAtMs(a.o))
        .map((x) => x.o);
    }
    if (sort === "public") {
      return scored
        .filter((x) => x.o.publicGallery)
        .sort((a, b) => meditatedAtMs(b.o) - meditatedAtMs(a.o))
        .map((x) => x.o);
    }
    if (sort === "all") {
      return scored.sort((a, b) => tsMs(b.o) - tsMs(a.o)).map((x) => x.o);
    }
    // recent — default for Meditated view; newest meditation first
    return scored
      .sort((a, b) => meditatedAtMs(b.o) - meditatedAtMs(a.o))
      .map((x) => x.o);
  }, [sourceOrders, shiftAvgs, sort, view]);

  const groupedByShift = useMemo(() => {
    if (view !== "unmeditated" || sort !== "shift") return null;
    const groups = [];
    let current = null;
    for (const o of sorted) {
      const key = o.shiftId || "no-shift";
      if (!current || current.key !== key) {
        const summary = shifts[key];
        const label = summary?.eventName || (key === "no-shift" ? "No shift" : key);
        current = { key, label, items: [] };
        groups.push(current);
      }
      current.items.push(o);
    }
    return groups;
  }, [sorted, shifts, sort, view]);

  const markedCount      = unmeditatedOrders.filter((o) => o.meditationMark).length;
  const unmeditatedCount = unmeditatedOrders.length;
  const meditatedCount   = meditatedOrders.length;

  return (
    <div style={{ background: BRAND.dark, minHeight: "100vh", color: BRAND.cream, fontFamily: FONT.body }}>
      <Header
        navigate={navigate}
        view={view}
        switchView={switchView}
        sort={sort}
        setSort={setSort}
        markedCount={markedCount}
        unmeditatedCount={unmeditatedCount}
        meditatedCount={meditatedCount}
      />

      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "1rem" }}>
        {!loaded && <p style={{ color: BRAND.muted }}>Loading…</p>}

        {loaded && view !== "shifts" && sorted.length === 0 && (
          <EmptyState view={view} sort={sort} totalUnmeditated={unmeditatedCount} totalMeditated={meditatedCount} switchView={switchView} setSort={setSort} />
        )}

        {loaded && view === "unmeditated" && sort === "shift" && groupedByShift && groupedByShift.map((g) => (
          <div key={g.key} style={{ marginBottom: "2rem" }}>
            <h3 style={{ color: BRAND.gold, fontSize: "0.95rem", letterSpacing: "0.04em", margin: "0 0 0.6rem", textTransform: "uppercase" }}>
              {g.label} <span style={{ color: BRAND.dim, fontWeight: "normal" }}>· {g.items.length}</span>
            </h3>
            <Grid orders={g.items} onTap={setEditing} />
          </div>
        ))}

        {loaded && view !== "shifts" && sorted.length > 0 && !(view === "unmeditated" && sort === "shift") && (
          <Grid orders={sorted} onTap={setEditing} />
        )}

        {loaded && view === "shifts" && (
          shiftRows.length === 0 ? (
            <div style={{ textAlign: "center", padding: "3rem 1rem", color: BRAND.muted, lineHeight: 1.55 }}>
              <div style={{ fontSize: "2rem", marginBottom: "0.6rem" }}>🔥</div>
              {sort === "meditated"
                ? "No shift meditations written yet. Pick a day and tell its story."
                : "No closed shifts here yet."}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              {shiftRows.map((s) => (
                <ShiftRow key={s.id} shift={s} onTap={() => setEditingShift(s)} />
              ))}
            </div>
          )
        )}
      </div>

      {editing && (
        <EditorModal order={editing} onClose={() => setEditing(null)} onSaved={() => {}} />
      )}
      {editingShift && (
        <ShiftEditorModal shift={editingShift} onClose={() => setEditingShift(null)} />
      )}
    </div>
  );
}

const UNMEDITATED_SORTS = [
  ["showpiece",   "⭐ Showpieces"],
  ["recommended", "✨ Recommended"],
  ["recent",      "🕒 Recent"],
  ["shift",       "📋 By shift"],
  ["marked",      "🔖 Marked"],
];
const MEDITATED_SORTS = [
  ["recent",      "🕒 Recently written"],
  ["pinned",      "📌 Pinned"],
  ["public",      "⭐ Public"],
  ["all",         "🗂 All"],
];
const SHIFT_SORTS = [
  ["all",         "🗂 All"],
  ["unmeditated", "🧘 To write"],
  ["meditated",   "📖 Written"],
];

function Header({ navigate, view, switchView, sort, setSort, markedCount, unmeditatedCount, meditatedCount }) {
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);

  const refreshDriftSample = async () => {
    if (syncing) return;
    setSyncing(true); setSyncMsg(null);
    try {
      // Refresh both public summary docs at once — drift pool AND meditations
      // list. Cheaper to do them together from Dan's perspective.
      const fns = getFunctions(getApp());
      const [driftRes, medRes] = await Promise.all([
        httpsCallable(fns, "triggerPublicPancakeSampleSync")({}),
        httpsCallable(fns, "triggerPublicMeditationsSync")({}),
      ]);
      setSyncMsg(`✓ Refreshed — ${driftRes.data?.itemCount ?? "?"} drift · ${medRes.data?.itemCount ?? "?"} meditations`);
      setTimeout(() => setSyncMsg(null), 4500);
    } catch (e) {
      setSyncMsg("✕ " + (e.message || "Sync failed"));
      setTimeout(() => setSyncMsg(null), 5000);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div style={{ background: BRAND.panel, borderBottom: `1px solid ${BRAND.border}`, padding: "0.75rem 1rem", position: "sticky", top: 0, zIndex: 10 }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, color: BRAND.cream, fontFamily: "var(--jop-script, serif)", fontSize: "1.6rem", fontWeight: 700 }}>
            Meditations
          </h1>
          {/* View tabs — the primary split between "what to meditate on next"
              and "what I've already written about." Counts show backlog size. */}
          <div style={{ display: "flex", gap: "0.35rem", marginLeft: "0.5rem" }}>
            <button onClick={() => switchView("unmeditated")} style={tabBtn(view === "unmeditated")}>
              🧘 To meditate <CountPill n={unmeditatedCount} active={view === "unmeditated"} />
            </button>
            <button onClick={() => switchView("meditated")} style={tabBtn(view === "meditated")}>
              📖 Meditated <CountPill n={meditatedCount} active={view === "meditated"} />
            </button>
            <button onClick={() => switchView("shifts")} style={tabBtn(view === "shifts")}>
              🔥 Shifts
            </button>
          </div>
          {markedCount > 0 && view === "unmeditated" && (
            <button onClick={() => setSort("marked")} style={{ ...sortBtn(sort === "marked"), background: "rgba(252,179,31,0.18)", borderColor: BRAND.gold, color: BRAND.gold }}>
              ✨ {markedCount} marked
            </button>
          )}
          {syncMsg && (
            <span style={{ color: syncMsg.startsWith("✓") ? "#70be44" : "#ff8a8a", fontSize: "0.78rem" }}>{syncMsg}</span>
          )}
        </div>
        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
          {(view === "unmeditated" ? UNMEDITATED_SORTS : view === "shifts" ? SHIFT_SORTS : MEDITATED_SORTS).map(([k, label]) => (
            <button key={k} onClick={() => setSort(k)} style={sortBtn(sort === k)}>{label}</button>
          ))}
          <button onClick={refreshDriftSample} disabled={syncing} style={{ ...btnGhost, opacity: syncing ? 0.5 : 1 }} title="Refreshes what public site visitors see: the hero drift pool AND the meditations list. Run after publishing new meditations or marking new public pieces.">
            {syncing ? "…" : "🌊 Refresh public site"}
          </button>
          <button onClick={() => navigate("/menu")} style={btnGhost}>← Menu</button>
        </div>
      </div>
    </div>
  );
}

function CountPill({ n, active }) {
  return (
    <span style={{
      marginLeft: "0.45rem",
      background: active ? "rgba(252,179,31,0.25)" : "rgba(255,218,173,0.1)",
      color: active ? "var(--jop-gold)" : "rgba(255,218,173,0.65)",
      borderRadius: "999px",
      padding: "0.1rem 0.45rem",
      fontSize: "0.7rem",
      fontWeight: 700,
      letterSpacing: "0.02em",
    }}>
      {n}
    </span>
  );
}

function Grid({ orders, onTap }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
      gap: "0.75rem",
    }}>
      {orders.map((o) => <Card key={o.id} order={o} onTap={() => onTap(o)} />)}
    </div>
  );
}

function Card({ order, onTap }) {
  const img = order.photo_cutout_url || order.photo_thumb_url || order.photo_url;
  const hasMeditation = !!order.meditation?.trim();
  const marked = !!order.meditationMark;
  const pinned = !!order.meditationPinned;

  return (
    <button onClick={onTap} style={{
      background: BRAND.panel, border: `1px solid ${marked ? BRAND.gold : BRAND.border}`,
      borderRadius: "10px", overflow: "hidden", cursor: "pointer", textAlign: "left",
      padding: 0, color: "inherit", position: "relative",
    }}>
      <div style={{ aspectRatio: "1 / 1", background: order.photo_cutout_url ? "transparent" : "#000" }}>
        {img && <img src={img} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: order.photo_cutout_url ? "contain" : "cover", display: "block" }} />}
      </div>
      <div style={{ padding: "0.5rem 0.6rem", display: "flex", flexDirection: "column", gap: "0.2rem" }}>
        <div style={{ color: BRAND.cream, fontWeight: 700, fontSize: "0.82rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {shortLabel(order)}
        </div>
        <div style={{ color: BRAND.dim, fontSize: "0.68rem" }}>
          {fmtDate(order.completedAt ?? order.photographedAt ?? order.createdAt)}
        </div>
        <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap", marginTop: "0.15rem" }}>
          {pinned && <Pill color={BRAND.pink}>📌 Pinned</Pill>}
          {marked && <Pill color={BRAND.gold}>✨ Marked</Pill>}
          {hasMeditation && <Pill color={BRAND.blue}>📝 Meditated</Pill>}
          {order.publicGallery && <Pill color={BRAND.cream}>⭐ Public</Pill>}
        </div>
      </div>
    </button>
  );
}

function Pill({ color, children }) {
  return (
    <span style={{
      fontSize: "0.6rem", padding: "0.12rem 0.4rem", borderRadius: "999px",
      border: `1px solid ${color}55`, color, lineHeight: 1.4,
    }}>{children}</span>
  );
}

function EditorModal({ order: initialOrder, onClose, onSaved }) {
  // Subscribe to the live order doc so toggle flips (public, pinned, mark)
  // reflect immediately in the UI. Without this, the modal holds a stale
  // snapshot from the moment the card was tapped — Dan saw toggles not
  // update visually until page refresh.
  const [liveOrder, setLiveOrder] = useState(initialOrder);
  useEffect(() => {
    if (!initialOrder?.id) return;
    const unsub = onSnapshot(doc(db, "orders", initialOrder.id), (snap) => {
      if (snap.exists()) setLiveOrder({ id: snap.id, ...snap.data() });
    });
    return unsub;
  }, [initialOrder?.id]);
  const order = liveOrder;

  const [text, setText]       = useState(order.meditation || "");
  const [busy, setBusy]       = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [err, setErr]         = useState(null);
  const { recording, interim, speechSupported, startRecording, stopRecording } =
    useDictation(text, setText, setErr);
  // Auto-grow the meditation textarea as dictation piles up. Dan edits the
  // transcript for flow + Haiku typos — easier to scan when the whole thing
  // is visible at once instead of buried behind a scrollbar.
  const textareaRef = useRef(null);
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);
  const marked = !!order.meditationMark;
  const pinned = !!order.meditationPinned;
  const isPublic = !!order.publicGallery;
  const history = order.meditationHistory || [];

  const polish = async () => {
    if (polishing || !text.trim()) return;
    setPolishing(true); setErr(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "polishMeditation");
      const res = await fn({ text });
      const polished = res.data?.polished;
      if (polished) setText(polished);
    } catch (e) {
      setErr(e.message || "Polish failed");
    } finally {
      setPolishing(false);
    }
  };

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const trimmed = text.trim();
      const prev = (order.meditation || "").trim();
      const updates = {
        meditation: trimmed || null,
        meditatedAt: trimmed ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
      };
      // If we had a previous meditation, archive it before overwriting
      if (prev && prev !== trimmed) {
        updates.meditationHistory = [...history, { text: prev, savedAt: order.meditatedAt || null, archivedAt: new Date() }];
      }
      // Saving a meditation clears the mark — its purpose was to remind us
      if (trimmed && marked) updates.meditationMark = false;
      // Auto-publish on first meditation — if a pancake earned a reflection,
      // it's worth reading. Dan can still toggle ⭐ Public off afterward.
      if (trimmed && !prev && !isPublic) updates.publicGallery = true;
      await updateDoc(doc(db, "orders", order.id), updates);
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleMark = async () => {
    await updateDoc(doc(db, "orders", order.id), { meditationMark: !marked, updatedAt: serverTimestamp() });
  };
  const togglePinned = async () => {
    await updateDoc(doc(db, "orders", order.id), { meditationPinned: !pinned, updatedAt: serverTimestamp() });
  };
  const togglePublic = async () => {
    await updateDoc(doc(db, "orders", order.id), { publicGallery: !isPublic, updatedAt: serverTimestamp() });
  };

  const img = order.photo_cutout_url || order.photo_url;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: BRAND.dark, border: `1px solid ${BRAND.border}`, borderRadius: "14px", maxWidth: "640px", width: "100%", maxHeight: "92vh", overflow: "auto", padding: "1.1rem" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.8rem" }}>
          <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.05em" }}>
            🧘 MEDITATION · {shortLabel(order)}
          </div>
          <button onClick={onClose} style={btnGhost}>×</button>
        </div>

        {img && (
          <div style={{
            // No hard cap on height — the whole pancake needs to be visible
            // for Dan to meditate on it. Modal container already caps itself
            // at 92vh and handles overflow, so tall cutouts just stretch
            // within that budget.
            margin: "0 auto 0.9rem",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: order.photo_cutout_url ? "0.5rem" : 0,
            background: order.photo_cutout_url ? "transparent" : "#000",
            borderRadius: "10px",
            overflow: order.photo_cutout_url ? "visible" : "hidden",
          }}>
            <img src={img} alt="" style={{
              maxWidth: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              display: "block",
              filter: order.photo_cutout_url ? "drop-shadow(0 16px 32px rgba(0,0,0,0.55))" : "none",
            }} />
          </div>
        )}

        {/* Vibes — surfaced above the text area so Dan sees the emotional
            shorthand he gave the pancake before (or right after) writing
            the meditation. Matches placement in the customer keepsake +
            gallery detail. */}
        {(order.artistTags?.length > 0 || order.artistTagsReflection?.length > 0) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", marginBottom: "0.85rem", justifyContent: "center" }}>
            {(order.artistTags || []).map((id) => (
              <VibesPill key={`g-${id}`} id={id} kind="gut" />
            ))}
            {(order.artistTagsReflection || []).map((id) => (
              <VibesPill key={`r-${id}`} id={id} kind="reflection" />
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
          {speechSupported ? (
            <button
              onClick={recording ? stopRecording : startRecording}
              disabled={polishing}
              style={{
                background: recording ? "rgba(239,83,126,0.18)" : "rgba(74,164,225,0.15)",
                color: recording ? BRAND.pink : BRAND.blue,
                border: `1px solid ${recording ? BRAND.pink : BRAND.blue}`,
                borderRadius: "999px", padding: "0.4rem 0.85rem", cursor: "pointer",
                fontSize: "0.82rem", fontWeight: 600,
                display: "inline-flex", alignItems: "center", gap: "0.4rem",
              }}
            >
              <span style={{
                width: "8px", height: "8px", borderRadius: "50%",
                background: recording ? BRAND.pink : "transparent",
                border: recording ? "none" : `1px solid ${BRAND.blue}`,
                animation: recording ? "pulse 1.2s ease-in-out infinite" : "none",
              }} />
              {recording ? "Stop" : "🎙 Dictate"}
            </button>
          ) : (
            <span style={{ color: BRAND.dim, fontSize: "0.75rem" }}>
              Dictation not supported in this browser (try Chrome or Safari).
            </span>
          )}
          <button
            onClick={polish}
            disabled={polishing || recording || !text.trim()}
            style={{
              background: "rgba(252,179,31,0.12)",
              color: polishing ? BRAND.dim : BRAND.gold,
              border: `1px solid ${BRAND.gold}55`,
              borderRadius: "999px", padding: "0.4rem 0.85rem",
              cursor: polishing || recording || !text.trim() ? "default" : "pointer",
              fontSize: "0.82rem", fontWeight: 600,
              opacity: !text.trim() && !polishing ? 0.5 : 1,
            }}
            title="Clean up dictation with Claude — preserves your voice"
          >
            {polishing ? "Polishing…" : "✨ Polish"}
          </button>
          {recording && interim && (
            <span style={{ color: BRAND.dim, fontSize: "0.72rem", fontStyle: "italic" }}>listening…</span>
          )}
        </div>

        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          placeholder={recording ? "Listening — speak freely…" : "What's the story? What about this piece is worth remembering?"}
          style={{
            width: "100%", boxSizing: "border-box",
            background: BRAND.panel, color: BRAND.cream,
            border: `1px solid ${recording ? BRAND.pink : BRAND.border}`, borderRadius: "10px",
            padding: "0.75rem", fontFamily: "inherit", fontSize: "0.95rem", lineHeight: 1.45,
            resize: "vertical",
            overflow: "hidden",
            minHeight: "8rem",
            transition: "border-color 0.2s",
          }}
        />

        {err && <div style={{ color: "#ff8a8a", fontSize: "0.82rem", marginTop: "0.45rem" }}>{err}</div>}

        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.7rem", flexWrap: "wrap" }}>
          <button onClick={save} disabled={busy} style={primaryBtn}>
            {busy ? "Saving…" : "💾 Save meditation"}
          </button>
          <button onClick={toggleMark} style={toggleBtn(marked, BRAND.gold)}>
            {marked ? "✨ Marked" : "✨ Mark"}
          </button>
          <button onClick={togglePinned} style={toggleBtn(pinned, BRAND.pink)}>
            {pinned ? "📌 Pinned" : "📌 Pin to site"}
          </button>
          <button onClick={togglePublic} style={toggleBtn(isPublic, BRAND.cream)}>
            {isPublic ? "⭐ Public" : "☆ Public"}
          </button>
          <button
            onClick={() => window.open(`/preview-keepsake/${order.id}`, "_blank")}
            style={toggleBtn(false, BRAND.blue)}
            title="Open the full customer keepsake for this pancake in a new tab"
          >
            🔍 Preview
          </button>
        </div>

        {history.length > 0 && (
          <details style={{ marginTop: "1rem", color: BRAND.muted }}>
            <summary style={{ cursor: "pointer", color: BRAND.dim, fontSize: "0.78rem" }}>
              📜 Past meditations ({history.length})
            </summary>
            <div style={{ marginTop: "0.5rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {history.slice().reverse().map((h, i) => (
                <div key={i} style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.55rem 0.7rem" }}>
                  <div style={{ color: BRAND.cream, fontSize: "0.85rem", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>{h.text}</div>
                  <div style={{ color: BRAND.dim, fontSize: "0.65rem", marginTop: "0.25rem" }}>
                    {fmtDate(h.savedAt) || "earlier"} → archived {fmtDate(h.archivedAt)}
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

// Web SpeechRecognition dictation, shared by the pancake + shift editor
// modals. `text` is only read at start (as the base to append onto);
// transcription streams into setText, interim noise trimmed on stop.
function useDictation(text, setText, setErr) {
  const [recording, setRecording] = useState(false);
  const [interim, setInterim] = useState("");
  const recogRef      = useRef(null);
  const baseTextRef   = useRef("");
  const finalChunkRef = useRef("");

  const speechSupported = typeof window !== "undefined" &&
    !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const startRecording = () => {
    if (recording || !speechSupported) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR();
    r.lang = "en-US";
    r.continuous = true;
    r.interimResults = true;

    baseTextRef.current = text ? text.replace(/\s+$/, "") + (text.trim() ? " " : "") : "";
    finalChunkRef.current = "";
    setInterim("");

    r.onresult = (ev) => {
      let interimChunk = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const transcript = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) {
          finalChunkRef.current += (finalChunkRef.current ? " " : "") + transcript.trim();
        } else {
          interimChunk += transcript;
        }
      }
      setText(baseTextRef.current + finalChunkRef.current + (interimChunk ? (finalChunkRef.current ? " " : "") + interimChunk : ""));
      setInterim(interimChunk);
    };
    r.onerror = (ev) => {
      console.warn("SpeechRecognition error", ev.error);
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
        setErr("Microphone permission denied. Allow mic access in browser settings.");
      } else if (ev.error === "no-speech") {
        // ignore — common pause
      } else {
        setErr("Dictation error: " + ev.error);
      }
    };
    r.onend = () => {
      setRecording(false);
      setInterim("");
      // Commit final text without trailing interim noise
      setText(baseTextRef.current + finalChunkRef.current);
    };

    try {
      r.start();
      recogRef.current = r;
      setRecording(true);
      setErr(null);
    } catch (e) {
      setErr("Couldn't start dictation: " + e.message);
    }
  };

  const stopRecording = () => {
    try { recogRef.current?.stop(); } catch {}
  };

  useEffect(() => {
    return () => { try { recogRef.current?.stop(); } catch {} };
  }, []);

  return { recording, interim, speechSupported, startRecording, stopRecording };
}

// One closed shift in the Shifts tab — the day as a journal entry to open.
function ShiftRow({ shift, onTap }) {
  const hasMeditation = !!shift.meditation?.trim();
  const pinned = !!shift.meditationPinned;
  const label = shift.eventName || shift.venueName || "Shift";
  const sub = [
    fmtDate(shift.closedAt),
    shift.venueName && shift.venueName !== label ? shift.venueName : null,
    shift.ordersCompleted != null ? `${shift.ordersCompleted} 🥞` : null,
  ].filter(Boolean).join(" · ");

  return (
    <button onClick={onTap} style={{
      background: BRAND.panel,
      border: `1px solid ${hasMeditation ? "rgba(74,164,225,0.4)" : BRAND.border}`,
      borderRadius: "10px", cursor: "pointer", textAlign: "left",
      padding: "0.7rem 0.9rem", color: "inherit",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem",
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: BRAND.cream, fontWeight: 700, fontSize: "0.92rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}
        </div>
        <div style={{ color: BRAND.dim, fontSize: "0.72rem", marginTop: "0.15rem" }}>{sub}</div>
      </div>
      <div style={{ display: "flex", gap: "0.3rem", flexShrink: 0 }}>
        {pinned && <Pill color={BRAND.pink}>📌 Pinned</Pill>}
        {hasMeditation
          ? <Pill color={BRAND.blue}>📝 Meditated</Pill>
          : <Pill color={BRAND.gold}>🧘 To write</Pill>}
      </div>
    </button>
  );
}

// Shift meditation editor — mirrors EditorModal but writes to the shift doc.
// The recap collage (PII-safe publicShiftRecaps) shows the day's pancakes so
// Dan writes with the whole griddle in view. Saving triggers the server-side
// recap rebuild + publicMeditations resync automatically.
function ShiftEditorModal({ shift: initialShift, onClose }) {
  const [liveShift, setLiveShift] = useState(initialShift);
  useEffect(() => {
    if (!initialShift?.id) return;
    const unsub = onSnapshot(doc(db, "shifts", initialShift.id), (snap) => {
      if (snap.exists()) setLiveShift({ id: snap.id, ...snap.data() });
    });
    return unsub;
  }, [initialShift?.id]);
  const shift = liveShift;

  const [text, setText]           = useState(shift.meditation || "");
  const [busy, setBusy]           = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [err, setErr]             = useState(null);
  const { recording, interim, speechSupported, startRecording, stopRecording } =
    useDictation(text, setText, setErr);

  const textareaRef = useRef(null);
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  // The day's pancakes, from the PII-safe recap doc (may not exist for very
  // old shifts — the collage strip just stays empty).
  const [recapPancakes, setRecapPancakes] = useState([]);
  useEffect(() => {
    if (!initialShift?.id) return;
    let cancel = false;
    getDoc(doc(db, "publicShiftRecaps", initialShift.id))
      .then((snap) => {
        if (!cancel && snap.exists()) setRecapPancakes(snap.data()?.pancakes || []);
      })
      .catch(() => {});
    return () => { cancel = true; };
  }, [initialShift?.id]);

  const pinned  = !!shift.meditationPinned;
  const history = shift.meditationHistory || [];
  const label   = shift.eventName || shift.venueName || "Shift";

  const polish = async () => {
    if (polishing || !text.trim()) return;
    setPolishing(true); setErr(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "polishMeditation");
      const res = await fn({ text });
      const polished = res.data?.polished;
      if (polished) setText(polished);
    } catch (e) {
      setErr(e.message || "Polish failed");
    } finally {
      setPolishing(false);
    }
  };

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const trimmed = text.trim();
      const prev = (shift.meditation || "").trim();
      const updates = {
        meditation: trimmed || null,
        meditatedAt: trimmed ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
      };
      if (prev && prev !== trimmed) {
        updates.meditationHistory = [...history, { text: prev, savedAt: shift.meditatedAt || null, archivedAt: new Date() }];
      }
      await updateDoc(doc(db, "shifts", shift.id), updates);
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const togglePinned = async () => {
    await updateDoc(doc(db, "shifts", shift.id), { meditationPinned: !pinned, updatedAt: serverTimestamp() });
  };

  const thumbs = recapPancakes
    .filter((p) => p.thumbUrl || p.cutoutUrl || p.photoUrl)
    .slice(0, 12);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: BRAND.dark, border: `1px solid ${BRAND.border}`, borderRadius: "14px", maxWidth: "640px", width: "100%", maxHeight: "92vh", overflow: "auto", padding: "1.1rem" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.8rem" }}>
          <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.05em" }}>
            🔥 SHIFT MEDITATION · {label}
          </div>
          <button onClick={onClose} style={btnGhost}>×</button>
        </div>

        <div style={{ color: BRAND.dim, fontSize: "0.75rem", marginBottom: "0.7rem" }}>
          {fmtDate(shift.closedAt)}{shift.ordersCompleted != null ? ` · ${shift.ordersCompleted} pancakes` : ""}
        </div>

        {thumbs.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", justifyContent: "center", marginBottom: "0.9rem" }}>
            {thumbs.map((p, i) => (
              <img
                key={p.orderId || i}
                src={p.thumbUrl || p.cutoutUrl || p.photoUrl}
                alt={p.title || ""}
                loading="lazy"
                style={{ width: "64px", height: "64px", objectFit: "contain", filter: "drop-shadow(0 6px 12px rgba(0,0,0,0.5))" }}
              />
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
          {speechSupported ? (
            <button
              onClick={recording ? stopRecording : startRecording}
              disabled={polishing}
              style={{
                background: recording ? "rgba(239,83,126,0.18)" : "rgba(74,164,225,0.15)",
                color: recording ? BRAND.pink : BRAND.blue,
                border: `1px solid ${recording ? BRAND.pink : BRAND.blue}`,
                borderRadius: "999px", padding: "0.4rem 0.85rem", cursor: "pointer",
                fontSize: "0.82rem", fontWeight: 600,
                display: "inline-flex", alignItems: "center", gap: "0.4rem",
              }}
            >
              <span style={{
                width: "8px", height: "8px", borderRadius: "50%",
                background: recording ? BRAND.pink : "transparent",
                border: recording ? "none" : `1px solid ${BRAND.blue}`,
                animation: recording ? "pulse 1.2s ease-in-out infinite" : "none",
              }} />
              {recording ? "Stop" : "🎙 Dictate"}
            </button>
          ) : (
            <span style={{ color: BRAND.dim, fontSize: "0.75rem" }}>
              Dictation not supported in this browser (try Chrome or Safari).
            </span>
          )}
          <button
            onClick={polish}
            disabled={polishing || recording || !text.trim()}
            style={{
              background: "rgba(252,179,31,0.12)",
              color: polishing ? BRAND.dim : BRAND.gold,
              border: `1px solid ${BRAND.gold}55`,
              borderRadius: "999px", padding: "0.4rem 0.85rem",
              cursor: polishing || recording || !text.trim() ? "default" : "pointer",
              fontSize: "0.82rem", fontWeight: 600,
              opacity: !text.trim() && !polishing ? 0.5 : 1,
            }}
            title="Clean up dictation with Claude — preserves your voice"
          >
            {polishing ? "Polishing…" : "✨ Polish"}
          </button>
          {recording && interim && (
            <span style={{ color: BRAND.dim, fontSize: "0.72rem", fontStyle: "italic" }}>listening…</span>
          )}
        </div>

        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          placeholder={recording ? "Listening — speak freely…" : "What happened around the griddle today? The stories between the flips."}
          style={{
            width: "100%", boxSizing: "border-box",
            background: BRAND.panel, color: BRAND.cream,
            border: `1px solid ${recording ? BRAND.pink : BRAND.border}`, borderRadius: "10px",
            padding: "0.75rem", fontFamily: "inherit", fontSize: "0.95rem", lineHeight: 1.45,
            resize: "vertical",
            overflow: "hidden",
            minHeight: "8rem",
            transition: "border-color 0.2s",
          }}
        />

        {err && <div style={{ color: "#ff8a8a", fontSize: "0.82rem", marginTop: "0.45rem" }}>{err}</div>}

        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.7rem", flexWrap: "wrap" }}>
          <button onClick={save} disabled={busy} style={primaryBtn}>
            {busy ? "Saving…" : "💾 Save meditation"}
          </button>
          <button onClick={togglePinned} style={toggleBtn(pinned, BRAND.pink)}>
            {pinned ? "📌 Pinned" : "📌 Pin to site"}
          </button>
          <button
            onClick={() => window.open(`/m/shift/${shift.id}`, "_blank")}
            style={toggleBtn(false, BRAND.blue)}
            title="Open the public page for this shift meditation (live a few seconds after saving)"
          >
            🔍 Preview
          </button>
        </div>

        {history.length > 0 && (
          <details style={{ marginTop: "1rem", color: BRAND.muted }}>
            <summary style={{ cursor: "pointer", color: BRAND.dim, fontSize: "0.78rem" }}>
              📜 Past meditations ({history.length})
            </summary>
            <div style={{ marginTop: "0.5rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {history.slice().reverse().map((h, i) => (
                <div key={i} style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.55rem 0.7rem" }}>
                  <div style={{ color: BRAND.cream, fontSize: "0.85rem", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>{h.text}</div>
                  <div style={{ color: BRAND.dim, fontSize: "0.65rem", marginTop: "0.25rem" }}>
                    {fmtDate(h.savedAt) || "earlier"} → archived {fmtDate(h.archivedAt)}
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

const btnGhost = {
  background: "transparent", color: "rgba(255,218,173,0.7)",
  border: "1px solid rgba(255,218,173,0.2)", borderRadius: "8px",
  padding: "0.35rem 0.7rem", cursor: "pointer", fontSize: "0.82rem",
};

const primaryBtn = {
  background: "#fcb31f", color: "#0a0f1c", border: "none",
  borderRadius: "10px", padding: "0.55rem 1rem", fontWeight: 700,
  cursor: "pointer", fontSize: "0.9rem",
};

function sortBtn(active) {
  return {
    background: active ? "rgba(74,164,225,0.18)" : "transparent",
    color: active ? "#4aa4e1" : "rgba(255,218,173,0.6)",
    border: `1px solid ${active ? "#4aa4e1" : "rgba(255,218,173,0.18)"}`,
    borderRadius: "999px", padding: "0.3rem 0.75rem",
    fontSize: "0.78rem", cursor: "pointer",
  };
}

function tabBtn(active) {
  return {
    background: active ? "rgba(252,179,31,0.15)" : "transparent",
    color: active ? "var(--jop-gold)" : "rgba(255,218,173,0.65)",
    border: `1px solid ${active ? "var(--jop-gold)" : "rgba(255,218,173,0.22)"}`,
    borderRadius: "999px",
    padding: "0.35rem 0.9rem",
    fontSize: "0.82rem",
    fontWeight: 700,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    letterSpacing: "0.02em",
  };
}

function toggleBtn(active, color) {
  return {
    background: active ? `${color}28` : "transparent",
    color: active ? color : "rgba(255,218,173,0.55)",
    border: `1px solid ${active ? color : "rgba(255,218,173,0.2)"}`,
    borderRadius: "10px", padding: "0.5rem 0.85rem", cursor: "pointer",
    fontSize: "0.85rem", fontWeight: 600,
  };
}

function tsMs(o) {
  const ts = o.completedAt ?? o.photographedAt ?? o.createdAt;
  return ts?.toMillis?.() ?? 0;
}

function meditatedAtMs(o) {
  return o.meditatedAt?.toMillis?.() ?? tsMs(o);
}

// View-specific empty-state copy. Leans into the achievement-language feel
// Dan asked for rather than a generic "nothing here."
function EmptyState({ view, sort, totalUnmeditated, totalMeditated, switchView, setSort }) {
  const base = {
    textAlign: "center",
    padding: "3rem 1rem",
    color: "rgba(255,218,173,0.7)",
    fontSize: "0.95rem",
    lineHeight: 1.55,
  };
  const link = {
    background: "transparent",
    color: "var(--jop-gold)",
    border: "1px solid rgba(252,179,31,0.45)",
    borderRadius: "999px",
    padding: "0.4rem 0.9rem",
    fontSize: "0.82rem",
    fontWeight: 700,
    cursor: "pointer",
    marginTop: "0.75rem",
    fontFamily: "inherit",
  };

  if (view === "unmeditated") {
    if (totalUnmeditated === 0) {
      return (
        <div style={base}>
          <div style={{ fontSize: "2rem", marginBottom: "0.6rem" }}>🧘</div>
          <div style={{ color: "var(--jop-cream)", fontWeight: 700, marginBottom: "0.3rem" }}>You've meditated on every pancake in the database.</div>
          <div>That's wild. When new work comes in, it'll land here.</div>
        </div>
      );
    }
    if (sort === "showpiece") {
      return (
        <div style={base}>
          <div style={{ fontSize: "1.4rem", marginBottom: "0.4rem" }}>⭐</div>
          <div>No starred showpieces are waiting on a meditation.</div>
          <div style={{ fontSize: "0.82rem", color: "rgba(255,218,173,0.5)", marginTop: "0.3rem" }}>
            Tap the ⭐ on a pancake in the Library to mark it as a showpiece worth reflecting on.
          </div>
          <button onClick={() => setSort("recommended")} style={link}>✨ Browse recommended</button>
        </div>
      );
    }
    if (sort === "marked") {
      return (
        <div style={base}>
          <div style={{ fontSize: "1.4rem", marginBottom: "0.4rem" }}>🔖</div>
          <div>No pancakes flagged for meditation right now.</div>
          <div style={{ fontSize: "0.82rem", color: "rgba(255,218,173,0.5)", marginTop: "0.3rem" }}>
            Tap ✨ Mark on any piece in the queue (or from the artist view) to pin it here.
          </div>
        </div>
      );
    }
    // Other unmeditated sort modes with 0 results is unlikely — fall through
    return <div style={base}>Nothing matches this filter yet.</div>;
  }

  // Meditated view
  if (totalMeditated === 0) {
    return (
      <div style={base}>
        <div style={{ fontSize: "2rem", marginBottom: "0.6rem" }}>📖</div>
        <div style={{ color: "var(--jop-cream)", fontWeight: 700, marginBottom: "0.3rem" }}>No meditations yet.</div>
        <div>Pop over to the <strong>To meditate</strong> tab and write about a pancake.</div>
        <button onClick={() => switchView("unmeditated")} style={link}>🧘 Go to queue</button>
      </div>
    );
  }
  if (sort === "pinned") {
    return <div style={base}>No meditations pinned to the public site yet. Open one and tap 📌 Pin.</div>;
  }
  if (sort === "public") {
    return <div style={base}>No meditations marked public yet. Open one and tap ⭐ Public.</div>;
  }
  return <div style={base}>Nothing matches this filter yet.</div>;
}

function fmtDate(ts) {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : ts instanceof Date ? ts : new Date(ts);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Vibes shorthand — must stay in sync with GalleryView's ARTIST_TAGS.
const ARTIST_TAGS = {
  fire:  { emoji: "🔥", label: "Fire"  },
  clean: { emoji: "🎯", label: "Clean" },
  magic: { emoji: "✨", label: "Magic" },
  lab:   { emoji: "🧪", label: "Lab"   },
  grind: { emoji: "💪", label: "Grind" },
  rough: { emoji: "🫠", label: "Rough" },
  cute:  { emoji: "🥰", label: "Cute"  },
  funny: { emoji: "😂", label: "Funny" },
};

// Compact vibe chip. `kind` distinguishes gut (outlined) vs reflection (filled)
// so Dan can see at a glance which side of the rating a tag came from.
function VibesPill({ id, kind }) {
  const tag = ARTIST_TAGS[id];
  if (!tag) return null;
  const filled = kind === "reflection";
  return (
    <span
      title={`${tag.label}${kind ? ` · ${kind}` : ""}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: "0.3rem",
        background: filled ? "rgba(252,179,31,0.2)" : "rgba(255,255,255,0.04)",
        border: `1px solid ${filled ? "rgba(252,179,31,0.55)" : "rgba(255,218,173,0.25)"}`,
        color: filled ? "#fcb31f" : "rgba(255,218,173,0.8)",
        borderRadius: "999px",
        padding: "0.22rem 0.55rem",
        fontSize: "0.72rem", fontWeight: 600,
        lineHeight: 1,
      }}
    >
      <span style={{ fontSize: "0.95rem" }}>{tag.emoji}</span>
      <span>{tag.label}</span>
    </span>
  );
}
